import {AxiosInstance, AxiosResponse} from 'axios';
import {action, computed, makeAutoObservable, runInAction} from 'mobx';
import {IResult} from 'services/Result';
import {IAnalyticsStore} from 'stores/AnalyticsStore';
import {IOrganization} from 'types/models/OrganizationModels';
import {AxiosResponseHelper} from 'utilities/AxiosResponseHelper';
import {NotificationsStore} from './NotificationsStore';
import { downloadTextFile } from "../utilities/SystemUtils";
import { EmptyGuid } from 'utilities/Constants';
import { ValidationType } from 'types/GlobalTypes';

export interface IOrganizationsStore {
  organizations: IOrganization[];
  selectedOrganization: IOrganization | null;
  isLoading: boolean;

  _notifications_store: NotificationsStore;
  _analytics_store: IAnalyticsStore;
  getOrganizations: (apiInstance: AxiosInstance, forceReload?: boolean) => Promise<void>;
  getSingleOrganization: (apiInstance: AxiosInstance, organizationId: string) => Promise<IOrganization | undefined>;
  setSelectedOrganization: (organization: IOrganization | null) => void;
  getOrganizationName: (apiInstance: AxiosInstance, orgId: string) => Promise<string>;
}

export class OrganizationsStore implements IOrganizationsStore {
  organizations: IOrganization[] = [];
  selectedOrganization: IOrganization | null = null;
  isLoading: boolean = false;

  _notifications_store: NotificationsStore;
  _analytics_store: IAnalyticsStore;

  constructor(notificationsStore: NotificationsStore, analytics_store: IAnalyticsStore) {
    this._notifications_store = notificationsStore;
    this._analytics_store = analytics_store;
    makeAutoObservable(this);
  }
  
  @action
  public async getOrganizations(apiInstance: AxiosInstance, forceReload?: boolean) {
    if (!forceReload && this.organizations.length > 0) {
      return;
    }

    runInAction(() => { this.isLoading = true; });
    const response = await apiInstance.get<IResult<IOrganization[]>>('organization');

    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) {
      runInAction(() => { this.organizations = response.data.data ?? []; });
    } else {
      let errorMsg = 'Cannot retrieve organization list.'
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
      this._notifications_store.addToast(
        'Error',
        errorMsg,
        'error'
      );
    }
    runInAction(() => { this.isLoading = false; });
  }

  public IsOrganizationNameInvalid(name: string, id: string): boolean | string {
    if (!name || name.trim() === '') {
      return 'IS_EMPTY';
    }

    if (this.organizations.some(org => org.name === name && org.id !== id)) {
      return 'IS_DUPLICATED';
    }

    return false;
  }


  public IsOrganizationShortNameInvalid(shortName: string, id: string): boolean | string {
    if (!shortName || shortName.trim() === '') {
      return 'IS_EMPTY';
    }

    if (this.organizations.some(org => org.shortName === shortName && org.id !== id)) {
      return 'IS_DUPLICATED';
    }

    return false;
  }



  public GetEmptyOrganization(): IOrganization {
    return {
      id: EmptyGuid,
      name: '',
      shortName: '',
      externalId: ''
    } as IOrganization;
  }

  @action
  public async addOrUpdateOrganization(apiInstance: AxiosInstance, organization?: IOrganization) {
    if (!organization) {
      this._notifications_store.addToast(
        'Error',
        'No organization selected for update.',
        'error'
      );
      return;
    }

    runInAction(() => { this.isLoading = true; });

    // If the organization has no ID, it's a new organization; otherwise, it's an update
    let response: AxiosResponse<IResult<IOrganization>> | undefined;
    if (!organization.id || organization.id === EmptyGuid) {  
      response = await apiInstance.post<IResult<IOrganization>>('organization', organization);
    }
    else {
      response = await apiInstance.put<IResult<IOrganization>>(`organization/${organization.id}`, organization);
    }

    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      this.getOrganizations(apiInstance, true);
      this._notifications_store.addToast(
        'Success',
        'Organization updated successfully.',
        'success'
      );
    } else {
      let errorMsg = 'Cannot update organization.'
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
      this._notifications_store.addToast(
        'Error',
        errorMsg,
        'error'
      );
    }
    runInAction(() => { this.isLoading = false; });
  }
  
  @computed
  public get organizationsToShow(): IOrganization[]{
    let filteredOrganizations = this.organizations;
    return filteredOrganizations;
  }

  public exportOrganizationsToCsv() {
    const filename = `OrganizationsExport.csv`;
    const organizationsToExport = this.organizationsToShow
    if (organizationsToExport.length === 0) {
      this._notifications_store.addToast(
        'Error',
        'There are no organizations to be exported',
        'error'
      );
      return;
    }

    this.downloadOrganizationsCsvFile(organizationsToExport, filename);
  }
  
  private downloadOrganizationsCsvFile(organizations: IOrganization[], filename: string) {
    const csvString = this.convertOrganizationsToCsvString(organizations);
    downloadTextFile(csvString, 'text/csv;charset=utf-8;', filename);
  }
  
  private convertOrganizationsToCsvString(data: IOrganization[]): string {
    if (data.length === 0){
      return "";
    } 
    const flattenObject = (obj: any, parentKey = ""): Record<string, any> => {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        let newKey = parentKey ? `${parentKey}.${key}` : key;
        
        if (typeof value === "object" && value !== null) {
          Object.assign(acc, flattenObject(value, newKey));
        } else {
          acc[newKey] = value;
        }

        return acc;
      }, {} as Record<string, any>);
    };
    
    const flattenedData = data.map((item) => flattenObject(item));

    const keys = Object.keys(flattenedData[0]).filter(
      (key) => !key.endsWith(".licenseType")
    );
    
    const rows = flattenedData.map((row) =>
      keys.map((key) => {
        const value = row[key];
        if (typeof value === "string") {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      })
    );
    const csvString = [keys.join(","), ...rows.map((row) => row.join(","))].join(
      "\n"
    );
    return csvString;
  };
  
  public async getOrganization(apiInstance: AxiosInstance, orgId: string) {
    if (!!!this.organizations || this.organizations.length === 0) {
      await this.getOrganizations(apiInstance, true);
      console.log("loaded Organizations!");
    }
    const org = this.organizations.find(o => o.id === orgId);

    console.log("returning Organization name");
    return org as IOrganization;
  }  

  public async getOrganizationName(apiInstance: AxiosInstance, orgId: string) {
    const org = await this.getOrganization(apiInstance, orgId);

    return org?.name ?? "- - -";
  }

  // I added below method in a later phase of development; os it is not used to get selected organization
  public async getSingleOrganization(apiInstance: AxiosInstance, organizationId: string) {
    const response = await apiInstance.get<IResult<IOrganization>>(`organization/${organizationId}`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) {
      return response.data.data;
    } else {
      let errorMsg = 'Cannot retrieve organization.'
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
      this._notifications_store.addToast(
        'Error',
        errorMsg,
        'error'
      );
    }
  }

  public setSelectedOrganization(organization: IOrganization | null) {
    this.selectedOrganization = organization;
  }
}
